import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Wheat, ChefHat, Leaf, ArrowDown } from "lucide-react";
import { buildWhatsAppLink } from "../data/menu";
import { BrandSeal, WheatStalk, Flourish } from "./brand/Ornaments";

const heroImage =
  "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

const indicators = [
  { icon: Wheat, label: "Produção Artesanal" },
  { icon: Leaf, label: "Ingredientes Selecionados" },
  { icon: ChefHat, label: "Receitas Tradicionais" },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0.4]);

  const scrollToCardapio = () => {
    document.getElementById("cardapio")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 z-0"
      >
        <img
          src={heroImage}
          alt="Prato de massa artesanal italiana"
          className="w-full h-full object-cover animate-ken-burns"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e1a13] via-[#0e1a13]/85 to-[#0e1a13]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1a13] via-transparent to-[#0e1a13]/40" />
      </motion.div>

      {/* Decorative wheat stalks */}
      <div className="absolute top-24 left-4 sm:left-10 z-10 text-[var(--sn-gold)] opacity-40 hidden md:block pointer-events-none">
        <WheatStalk className="w-16 h-32" />
      </div>
      <div className="absolute bottom-32 right-4 sm:right-10 z-10 text-[var(--sn-gold)] opacity-40 hidden md:block pointer-events-none">
        <WheatStalk className="w-16 h-32" flip />
      </div>

      <div className="relative z-20 max-w-7xl mx-auto w-full px-6 sm:px-8 lg:px-12 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center pt-24 pb-16">
        {/* Left column */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-12 bg-[var(--sn-gold)]" />
            <span className="text-[var(--sn-gold)] tracking-[0.32em] text-xs uppercase">
              Massas Artesanais · Desde 2015
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-display text-[2.6rem] sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02] text-[var(--sn-cream)] mb-6"
            data-testid="hero-headline"
          >
            A tradição italiana,{" "}
            <span className="italic gold-text">feita à mão</span>{" "}
            na sua mesa.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="text-[var(--sn-cream-soft)]/85 text-lg max-w-2xl mb-10 leading-relaxed font-body"
            data-testid="hero-subheadline"
          >
            Nhoque, Canelone, Rondelli e Manicaretti preparados com ingredientes
            selecionados e receitas autênticas. Sabor de família, no ponto certo,
            todo dia.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mb-14"
          >
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 bg-[var(--sn-whatsapp)] hover:bg-[var(--sn-whatsapp-hover)] text-white font-medium px-8 py-4 rounded-md transition-all hover:translate-y-[-2px] hover:shadow-[0_20px_40px_-15px_rgba(37,211,102,0.45)]"
              data-testid="hero-whatsapp-button"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                <path d="M20.52 3.48A11.85 11.85 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 22a9.93 9.93 0 0 1-5.07-1.39l-.36-.21-3.67.96.98-3.58-.23-.37A9.96 9.96 0 1 1 22 12c0 5.51-4.49 10-10 10Zm5.49-7.46c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.27-.47-2.42-1.5a9.05 9.05 0 0 1-1.67-2.07c-.18-.3-.02-.46.13-.62.14-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.2-.24-.58-.49-.5-.66-.5l-.56-.01c-.2 0-.52.08-.79.37-.27.3-1.03 1-1.03 2.45s1.06 2.85 1.21 3.05c.15.2 2.08 3.17 5.04 4.45.7.3 1.25.48 1.68.62.7.22 1.34.2 1.85.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
              </svg>
              Pedir pelo WhatsApp
            </a>

            <button
              onClick={scrollToCardapio}
              className="inline-flex items-center justify-center gap-3 border border-[var(--sn-gold)] text-[var(--sn-gold)] hover:bg-[var(--sn-gold)]/10 font-medium px-8 py-4 rounded-sm transition-all group"
              data-testid="hero-menu-button"
            >
              Ver Cardápio
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-5"
          >
            {indicators.map((ind, i) => (
              <motion.div
                key={ind.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95 + i * 0.12 }}
                className="flex items-center gap-3 border-t border-[var(--sn-border)] pt-4"
                data-testid={`hero-indicator-${i}`}
              >
                <ind.icon className="w-5 h-5 text-[var(--sn-gold)] shrink-0" />
                <span className="text-[var(--sn-cream-soft)]/85 text-sm tracking-wide">
                  {ind.label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right column — Brand seal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.5, ease: "easeOut" }}
          className="hidden lg:flex justify-center items-center relative"
        >
          <div className="absolute inset-0 rounded-full bg-[var(--sn-bg-surface)]/40 blur-3xl" />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 70, ease: "linear", repeat: Infinity }}
            className="relative"
          >
            <BrandSeal size={340} />
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-[var(--sn-gold)]/70"
      >
        <Flourish className="w-32 h-5" />
      </motion.div>
    </section>
  );
};

export default Hero;
